import { Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  map,
  shareReplay,
} from 'rxjs';
import {
  NETWORK_THRESHOLDS,
  NetworkHealthSnapshot,
  NetworkState,
  RequestSample,
} from '../app/shared/models/network-health.model';

/**
 * NetworkHealthService
 *
 * Central brain for POS network health monitoring.
 *
 * Responsibilities:
 * - Listens to browser online/offline events                → OFFLINE / RECOVERING / NORMAL
 * - Listens to navigator.connection change events           → SLOW (3G/slow-4G) / NORMAL (4G)
 * - Tracks consecutive network-level errors (HTTP status 0) → OFFLINE
 *
 * INTENTIONALLY NOT DONE:
 * - API response latency (durationMs) is NOT used to change state.
 * - API timeouts are NOT used to change state.
 * - Rolling window of request durations is NOT evaluated.
 * - DEGRADED / CRITICAL states are NOT triggered from request timing.
 *
 * IMPORTANT:
 * - This service is purely additive — it does NOT interfere with any HTTP
 *   requests, business logic, or existing services.
 * - It only OBSERVES and REPORTS. It never cancels or modifies requests.
 * - NORMAL state emits no UI notifications; components should only react
 *   to non-NORMAL states.
 */
@Injectable({ providedIn: 'root' })
export class NetworkHealthService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // ─── Connection Helpers ───────────────────────────────────────────────────

  private static getConnection(): any {
    if (
      typeof window !== 'undefined' &&
      typeof window.navigator !== 'undefined'
    ) {
      return (
        (window.navigator as any).connection ||
        (window.navigator as any).mozConnection ||
        (window.navigator as any).webkitConnection ||
        null
      );
    }
    return null;
  }

  /**
   * Returns true when the browser's Network Information API reports a
   * 3G / slow-2G / 2G connection — i.e. genuinely slow mobile network.
   * Does NOT consider API response times.
   */
  private static isConnectionSlow(conn: any): boolean {
    if (!conn) return false;
    const et = conn.effectiveType;
    // Chromium DevTools sets effectiveType to '2g' / 'slow-2g' on Slow 3G and '3g' on Fast 3G
    if (et === 'slow-2g' || et === '2g' || et === '3g') {
      return true;
    }
    // High round-trip time estimate (>= 500ms) also indicates 3G/slow connection
    // if (typeof conn.rtt === 'number' && conn.rtt >= 500) {
    //   return true;
    // }
    return false;
  }

  private static getInitialNetworkState(): NetworkState {
    if (
      typeof window !== 'undefined' &&
      typeof window.navigator !== 'undefined'
    ) {
      // 1. Check if browser is completely offline
      if (!window.navigator.onLine) {
        return NetworkState.OFFLINE;
      }
      // 2. Check if browser connection is throttled/slow (e.g. 3G selected in DevTools or on load)
      const conn = NetworkHealthService.getConnection();
      if (NetworkHealthService.isConnectionSlow(conn)) {
        return NetworkState.SLOW;
      }
    }
    return NetworkState.NORMAL;
  }

  // ─── Internal state ───────────────────────────────────────────────────────

  private readonly isOnlineSubject$ = new BehaviorSubject<boolean>(
    typeof window !== 'undefined' && typeof window.navigator !== 'undefined'
      ? window.navigator.onLine
      : true,
  );

  private readonly networkStateSubject$ = new BehaviorSubject<NetworkState>(
    NetworkHealthService.getInitialNetworkState(),
  );

  // Tracks consecutive HTTP status-0 errors to detect connection loss
  // when the browser's online event fires too late.
  private consecutiveErrorCount = 0;

  // Tracks if we are in a RECOVERING state pending confirmation
  private isRecovering = false;

  // ─── Public Observables ───────────────────────────────────────────────────

  /**
   * Current network state stream.
   * NORMAL = no UI shown. All other states = show indicator.
   */
  readonly networkState$: Observable<NetworkState> = this.networkStateSubject$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  /** Browser online/offline status. */
  readonly isOnline$: Observable<boolean> = this.isOnlineSubject$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  /**
   * Shortcut: emits true ONLY when state is OFFLINE.
   * Components can bind to this directly without importing the NetworkState enum.
   */
  readonly isOffline$: Observable<boolean> = this.networkStateSubject$.pipe(
    map((state) => state === NetworkState.OFFLINE),
    distinctUntilChanged(),
    shareReplay(1),
  );

  /**
   * Billing block signal.
   *
   * Emits true when the network is in a state that makes placing an order
   * unsafe or impossible:
   *   - OFFLINE  → no network at all
   *   - SLOW     → 3G / slow-4G connection detected by browser
   *
   * Note: DEGRADED and CRITICAL are NOT triggered by this service any more
   * (they were latency-based). Only browser connection type drives blocking.
   */
  readonly isOrderBlocked$: Observable<boolean> =
    this.networkStateSubject$.pipe(
      map(
        (state) =>
          state === NetworkState.OFFLINE || state === NetworkState.SLOW,
      ),
      distinctUntilChanged(),
      shareReplay(1),
    );

  /** Current state snapshot — for use in synchronous guards. */
  get currentStateSnapshot(): NetworkState {
    return this.networkStateSubject$.value;
  }

  /** Synchronous check: is ordering currently blocked (offline or 3G/slow). */
  get isOrderBlocked(): boolean {
    const s = this.networkStateSubject$.value;
    return s === NetworkState.OFFLINE || s === NetworkState.SLOW;
  }

  /** Synchronous check: is the browser offline. */
  get isOffline(): boolean {
    return this.networkStateSubject$.value === NetworkState.OFFLINE;
  }

  // ─── Constructor ──────────────────────────────────────────────────────────

  constructor(private readonly ngZone: NgZone) {
    this.registerBrowserEvents();
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Called by NetworkLatencyInterceptor for every monitored HTTP request.
   *
   * ONLY used here to detect consecutive network-level errors (HTTP status 0),
   * which indicate the connection dropped before the browser fired the offline event.
   *
   * API response duration (durationMs) and timeout flags are deliberately ignored.
   */
  reportRequest(sample: RequestSample): void {
    // Run outside Angular zone — no change detection triggered here
    this.ngZone.runOutsideAngular(() => {
      this.processRequestSample(sample);
    });
  }

  /** Synchronous snapshot of current state — safe for guards. */
  getSnapshot(): NetworkHealthSnapshot {
    return {
      state: this.networkStateSubject$.value,
      isOnline: this.isOnlineSubject$.value,
      avgLatencyMs: 0, // Not tracked — latency monitoring removed
      consecutiveFastCount: 0,
      consecutiveErrorCount: this.consecutiveErrorCount,
      rollingWindow: [],
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.networkStateSubject$.complete();
    this.isOnlineSubject$.complete();
    this.removeBrowserEvents();
  }

  // ─── Browser & Connection Event Listeners ─────────────────────────────────

  private readonly onOnline = (): void => {
    this.ngZone.run(() => {
      this.isOnlineSubject$.next(true);
      this.consecutiveErrorCount = 0;
      const conn = NetworkHealthService.getConnection();
      if (NetworkHealthService.isConnectionSlow(conn)) {
        // Came back online but on a 3G/slow connection
        this.setNetworkState(NetworkState.SLOW);
      } else if (this.networkStateSubject$.value === NetworkState.OFFLINE) {
        // Came back online on a normal connection — enter RECOVERING briefly
        this.setNetworkState(NetworkState.RECOVERING);
        this.isRecovering = true;
        this.scheduleRecoveryToNormal();
      }
    });
  };

  private readonly onOffline = (): void => {
    this.ngZone.run(() => {
      this.isOnlineSubject$.next(false);
      this.isRecovering = false;
      this.consecutiveErrorCount = 0;
      this.setNetworkState(NetworkState.OFFLINE);
    });
  };

  /**
   * Fires when the browser's Network Information API detects a connection type change.
   * This is the PRIMARY mechanism for SLOW ↔ NORMAL transitions.
   * (e.g. user switches DevTools throttle from "No throttling" → "Slow 3G")
   */
  private readonly onConnectionChange = (): void => {
    this.ngZone.run(() => {
      if (!window.navigator.onLine) {
        this.setNetworkState(NetworkState.OFFLINE);
        return;
      }
      const conn = NetworkHealthService.getConnection();
      if (NetworkHealthService.isConnectionSlow(conn)) {
        // 3G / slow-4G detected — mark as SLOW
        this.setNetworkState(NetworkState.SLOW);
      } else if (conn && conn.effectiveType === '4g') {
        // && (conn.rtt == null || conn.rtt < 500)
        // Returned to fast 4G — recover to NORMAL
        const currentState = this.networkStateSubject$.value;
        if (
          currentState === NetworkState.SLOW ||
          currentState === NetworkState.RECOVERING
        ) {
          this.isRecovering = false;
          this.setNetworkState(NetworkState.NORMAL);
        }
      }
    });
  };

  private registerBrowserEvents(): void {
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    const conn = NetworkHealthService.getConnection();
    if (conn && typeof conn.addEventListener === 'function') {
      conn.addEventListener('change', this.onConnectionChange);
    }
  }

  private removeBrowserEvents(): void {
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    const conn = NetworkHealthService.getConnection();
    if (conn && typeof conn.removeEventListener === 'function') {
      conn.removeEventListener('change', this.onConnectionChange);
    }
  }

  // ─── State Machine ────────────────────────────────────────────────────────

  /**
   * Processes a request sample from the interceptor.
   *
   * ONLY acts on genuine network-level failures (HTTP status 0).
   * API response time (durationMs) is completely ignored.
   * Timeout samples are completely ignored.
   */
  private processRequestSample(sample: RequestSample): void {
    // Only respond to genuine network-level errors (HTTP status 0).
    // isTimeout and slow durationMs are deliberately ignored.
    if (sample.isNetworkError) {
      this.consecutiveErrorCount++;

      if (!this.isOnlineSubject$.value) {
        // Browser already knows we're offline
        this.ngZone.run(() => this.setNetworkState(NetworkState.OFFLINE));
        return;
      }

      // Browser says online but we're getting network errors —
      // after N consecutive failures assume offline
      if (
        this.consecutiveErrorCount >=
        NETWORK_THRESHOLDS.CONSECUTIVE_ERROR_THRESHOLD
      ) {
        this.ngZone.run(() => this.setNetworkState(NetworkState.OFFLINE));
      }
      return;
    }

    // Successful request — reset consecutive error counter.
    // No latency / rolling-window evaluation.
    this.consecutiveErrorCount = 0;

    // If we were recovering and API calls are succeeding,
    // confirm recovery (scheduleRecoveryToNormal handles the debounce).
    if (
      this.isRecovering &&
      this.networkStateSubject$.value === NetworkState.RECOVERING
    ) {
      this.scheduleRecoveryToNormal();
    }
  }

  // Debounce timer handle for RECOVERING → NORMAL transition
  private recoveryDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Schedules a delayed promotion from RECOVERING → NORMAL.
   * Prevents flapping if the connection briefly drops and comes back.
   */
  private scheduleRecoveryToNormal(): void {
    if (this.recoveryDebounceTimer !== null) {
      return; // Already scheduled
    }
    this.recoveryDebounceTimer = setTimeout(() => {
      this.recoveryDebounceTimer = null;
      const currentState = this.networkStateSubject$.value;
      if (currentState === NetworkState.RECOVERING) {
        this.isRecovering = false;
        this.ngZone.run(() => this.setNetworkState(NetworkState.NORMAL));
      }
    }, NETWORK_THRESHOLDS.RECOVERY_DEBOUNCE_MS);
  }

  private setNetworkState(state: NetworkState): void {
    if (this.networkStateSubject$.value !== state) {
      this.networkStateSubject$.next(state);
    }
  }
}
